import React from 'react';
import { PauseIcon, PlayIcon, XMarkIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';

interface Membership {
  id: string;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'EXPIRED';
  tier: {
    id: string;
    name: string;
  };
  creatorProfile?: {
    displayName: string;
  };
  nextChargeDate?: string;
}

interface MembershipActionsProps {
  membership: Membership;
  onPause: (id: string) => Promise<void>;
  onResume: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
  onScheduleTierChange?: (id: string) => void;
  isLoading?: boolean;
  className?: string;
}

/**
 * Membership Actions Component
 * Provides pause, resume, and cancel actions for memberships
 */
const MembershipActions: React.FC<MembershipActionsProps> = ({
  membership,
  onPause,
  onResume,
  onCancel,
  onScheduleTierChange,
  isLoading = false,
  className = '',
}) => {
  const handlePause = async () => {
    if (confirm('Are you sure you want to pause this membership? You won\'t be charged until you resume.')) {
      await onPause(membership.id);
    }
  };

  const handleResume = async () => {
    if (confirm('Are you sure you want to resume this membership? You will be charged on the next billing cycle.')) {
      await onResume(membership.id);
    }
  };

  const handleCancel = async () => {
    if (confirm('Are you sure you want to cancel this membership? This action cannot be undone.')) {
      await onCancel(membership.id);
    }
  };

  if (membership.status === 'CANCELLED' || membership.status === 'EXPIRED') {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        {membership.status === 'CANCELLED' ? 'Cancelled' : 'Expired'}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {membership.status === 'ACTIVE' && (
        <>
          {onScheduleTierChange && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onScheduleTierChange(membership.id)}
              disabled={isLoading}
              leftIcon={<CalendarDaysIcon className="w-4 h-4" />}
            >
              Schedule Change
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePause}
            disabled={isLoading}
            leftIcon={<PauseIcon className="w-4 h-4" />}
          >
            Pause
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isLoading}
            leftIcon={<XMarkIcon className="w-4 h-4" />}
          >
            Cancel
          </Button>
        </>
      )}
      {membership.status === 'PAUSED' && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResume}
            disabled={isLoading}
            leftIcon={<PlayIcon className="w-4 h-4" />}
          >
            Resume
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isLoading}
            leftIcon={<XMarkIcon className="w-4 h-4" />}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );
};

export default MembershipActions;


